import React, {useEffect, useState} from "react";
import "./ChangeCardModal.scss";
import { Form, FormInstance, Modal, Button } from "antd";
import { error, success } from "../../../service/MessageService";
import {
  BillingAddressDto,
  LightCardDto,
  MemberDto, SubStatusDto,
  UpdateCardDto,
} from "../../../generated/dashboard-client";
import BillingAddress from "../billing/BillingAddress";
import BillingCard from "../billing/BillingCard";
import AccountsApi from "../../../api/AccountsApi";
import { handleServerError } from "../../../util/ErrorHandler";
import {States} from "../../../util/countries";
import {CardElement, useElements, useStripe} from "@stripe/react-stripe-js";
import {CreatePaymentMethodData} from "@stripe/stripe-js";

interface ChangeCardModalProps {
  billingAddress: BillingAddressDto;
  member: MemberDto;
  card: LightCardDto;
  visible: boolean;
  onClose: (card: UpdateCardDto, billingAddress: BillingAddressDto) => void;
}

export default function ChangeCardModal({
  billingAddress,
  member,
  visible,
  onClose,
}: ChangeCardModalProps) {
  let formRef = React.createRef<FormInstance>();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState(Object.keys(States)[0]);
  const [country, setCountry] = useState("");
  const [zip, setZip] = useState("");

  const [loading, setLoading] = useState(false);
  const [cardInvalid, setCardInvalid] = useState(false);

  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => {
    if (!visible) {
      setFirstName(billingAddress.first || "");
      setLastName(billingAddress.last || "");
      setAddress(billingAddress.address || "");
      setAddress2(billingAddress.address2 || "");
      setCity(billingAddress.city || "");
      setState(billingAddress.state || "");
      setCountry(billingAddress.country || "");
      setZip(billingAddress.zip || "");
    }
  }, [
    visible,
    billingAddress.first,
    billingAddress.last,
    billingAddress.address,
    billingAddress.address2,
    billingAddress.city,
    billingAddress.state,
    billingAddress.country,
    billingAddress.zip,
  ]);

  async function createPaymentMethod() {
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);

    const {error, paymentMethod} = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement
    } as CreatePaymentMethodData);

    if (error) {
      console.log('[error]', error);
      throw error;
    } else {
      return paymentMethod;
    }
  }

  async function onSaveClick() {
    const billingInfo = {
      first: firstName,
      last: lastName,
      address,
      address2,
      city,
      zip,
      state,
      country,
    } as BillingAddressDto;

    if (!state) {
      error("Please select a state.");
    }
    if (!country) {
      error("Please select a country.");
    }
    if (!address) {
      error("The address field is empty.");
    }
    if (!city) {
      error("The city field is empty.");
    }

    if (
      !firstName ||
      !lastName ||
      cardInvalid ||
      !address ||
      !city ||
      !zip ||
      cardInvalid ||
      !state ||
      !country
    )
      return;

    setLoading(true);

    const paymentMethod = await createPaymentMethod();

    const subDto = {
      card: {
        paymentMethodId: paymentMethod?.id,
      },
      country,
      zip
    } as SubStatusDto;

    const cardInfo = {
      card: {
        number: paymentMethod?.card?.last4,
        cvv: '',
        year: paymentMethod?.card?.exp_year,
        month: paymentMethod?.card?.exp_month,
      },
    } as UpdateCardDto;

    window.MHM.ProModal.changeBilling({memberId: member.id!, subDto}).then(() => {
      AccountsApi.saveFullBillingInfo(member.id!, {
        address: billingInfo,
      })
        .then(() => {
          setLoading(false);
          success("Payment information successfully updated!");
          onClose(cardInfo, billingInfo);
        })
        .catch((e) => {
          handleServerError(e);
          setLoading(false);
        });
    }).catch(() => {
      setLoading(false);
    });
  }

  function onCloseClick() {
    formRef.current!.resetFields();
    onClose({} as UpdateCardDto, {} as BillingAddressDto);
  }

  function onCardChange(complete: boolean) {
    setCardInvalid(!complete);
  }

  function onChangeCardClick(type: string, value: string) {
    switch (type) {
      case "firstName":
        setFirstName(value);
        return;
      case "lastName":
        setLastName(value);
        return;
      case "address":
        setAddress(value);
        return;
      case "address2":
        setAddress2(value);
        return;
      case "city":
        setCity(value);
        return;
      case "state":
        setState(value);
        formRef.current!.setFieldsValue({ state: value });
        return;
      case "zip":
        setZip(value);
        return;
      case "country":
        setCountry(value);
        formRef.current!.setFieldsValue({ country: value });
        return;
      default:
        return "";
    }
  }

  return (
    <Modal
      className={"change-card-modal clean one"}
      visible={visible}
      destroyOnClose={true}
      closable={false}
    >
      <header>
        <h2>Payment Information</h2>
      </header>

      <Form
        ref={formRef}
        initialValues={{
          first: firstName,
          last: lastName,
          address: address,
          address2: address2,
          zip: zip,
          state: state,
          city: city,
          country: country,
        }}
      >

        <BillingCard
          firstName={firstName}
          lastName={lastName}
          onChangeClick={onChangeCardClick}
          onCardChange={onCardChange}/>

        <BillingAddress
          address={address}
          address2={address2}
          city={city}
          state={state}
          zip={zip}
          country={country}
          onChangeClick={onChangeCardClick}
        />

        <footer>
            <Button
              className={"cancel"}
              size={"large"}
              onClick={onCloseClick}
            >
              <span>Cancel</span>
            </Button>
            <Button
              className={"primary"}
              size={"large"}
              onClick={onSaveClick}
              loading={loading}
            >
              <span>Save</span>
            </Button>
        </footer>
      </Form>
    </Modal>
  );
}
